import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, ShoppingBag, NotebookPen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { api } from "@/lib/api";

const DEMO = {
  demo: true,
  title: "2026 Montana Archery Elk",
  meta: "September • Unit 316 • Archery",
  readiness: 82,
  gearRemaining: 5,
  drawStatus: "Applied",
  fieldNotes: 3,
  species: "Elk",
  weapon: "Archery",
};

function ReadinessRing({ pct }) {
  const r = 34, c = 2 * Math.PI * r, off = c - (pct / 100) * c;
  return (
    <div className="relative w-[84px] h-[84px] shrink-0" data-testid="readiness-ring">
      <svg width="84" height="84" viewBox="0 0 84 84" aria-hidden="true">
        <circle cx="42" cy="42" r={r} fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="6" />
        <circle cx="42" cy="42" r={r} fill="none" stroke="#D95D39" strokeWidth="6" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 42 42)" />
      </svg>
      <span className="absolute inset-0 grid place-items-center font-sans font-bold text-bone text-[17px]">{pct}%</span>
    </div>
  );
}

export function MyHomeRangeDashboard() {
  const { isAuthed } = useAuth();
  const { setBuildHuntOpen, openAuth } = useUI();
  const [hunt, setHunt] = useState(DEMO);

  useEffect(() => {
    if (!isAuthed) { setHunt(DEMO); return; }
    api.huntSummary().then((d) => {
      const h = d.hunt;
      if (!h) { setHunt(DEMO); return; }
      setHunt({
        demo: false,
        title: h.title || "My Hunt",
        meta: h.meta || "In progress",
        readiness: typeof h.readiness === "number" ? h.readiness : null,
        gearRemaining: typeof h.gear_remaining === "number" ? h.gear_remaining : null,
        drawStatus: h.draw_status || "Planning",
        fieldNotes: typeof h.field_notes_count === "number" ? h.field_notes_count : 0,
        species: h.species,
        weapon: h.weapon,
        id: h.id,
      });
    }).catch(() => setHunt(DEMO));
  }, [isAuthed]);

  const cta = () => (isAuthed ? setBuildHuntOpen(true) : openAuth("register"));

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
      className="w-full max-w-[440px] bg-black/55 backdrop-blur-md border border-white/12 text-bone p-7 sm:p-8"
      data-testid="my-homerange-dashboard"
    >
      <div className="flex items-center justify-between">
        <span className="hr-overline text-bone/60">My HomeRange</span>
        {hunt.demo && <span className="text-[9px] uppercase tracking-wider text-bone/50 border border-white/20 px-1.5 py-0.5 rounded-sm" data-testid="dashboard-demo-badge">Sample</span>}
      </div>
      <h3 className="font-serif text-3xl sm:text-[34px] leading-tight mt-3" data-testid="dashboard-hunt-title">{hunt.title}</h3>
      <p className="text-bone/60 text-sm mt-1">{hunt.meta}</p>

      <div className="h-px bg-white/12 my-6" />

      <div className="flex items-center gap-5">
        {hunt.readiness != null ? <ReadinessRing pct={hunt.readiness} /> : (
          <div className="w-[84px] h-[84px] rounded-full border-2 border-white/15 grid place-items-center text-bone/50 text-xs text-center shrink-0">Not<br/>started</div>
        )}
        <div>
          <strong className="block text-lg tracking-wide">HUNT READY</strong>
          <small className="text-bone/60">{hunt.gearRemaining != null ? `${hunt.gearRemaining} gear items still recommended` : "Build your gear list to track readiness"}</small>
        </div>
      </div>

      <div className="h-px bg-white/12 my-6" />

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="flex flex-col items-center gap-1.5" data-testid="dashboard-draw">
          <Check size={18} className="text-blaze" />
          <span className="hr-overline text-bone/50 text-[9px]">Draw</span>
          <small className="text-bone/80">{hunt.drawStatus}</small>
        </div>
        <div className="flex flex-col items-center gap-1.5 border-x border-white/10" data-testid="dashboard-gear">
          <ShoppingBag size={18} className="text-blaze" />
          <span className="hr-overline text-bone/50 text-[9px]">Gear</span>
          <small className="text-bone/80">{hunt.gearRemaining != null ? `${hunt.gearRemaining} items left` : "—"}</small>
        </div>
        <div className="flex flex-col items-center gap-1.5" data-testid="dashboard-field-notes">
          <NotebookPen size={18} className="text-blaze" />
          <span className="hr-overline text-bone/50 text-[9px]">Field Notes</span>
          <small className="text-bone/80">{hunt.fieldNotes} {hunt.fieldNotes === 1 ? "entry" : "entries"}</small>
        </div>
      </div>

      <div className="h-px bg-white/12 my-6" />

      <div className="flex items-center justify-between gap-3">
        <button onClick={cta} className="inline-flex items-center gap-2 text-blaze font-semibold hover:text-blaze-light transition-colors" data-testid="dashboard-continue">
          {hunt.demo ? "Build my hunt" : "Continue my hunt"} <ArrowRight size={16} />
        </button>
        {(hunt.species || hunt.weapon) && (
          <a href={`/marketplace?${new URLSearchParams({ ...(hunt.species ? { species: hunt.species } : {}), ...(hunt.weapon ? { weapon: hunt.weapon } : {}), hunt: hunt.title }).toString()}`}
            className="inline-flex items-center gap-1.5 text-bone/70 hover:text-blaze text-sm font-medium transition-colors" data-testid="dashboard-gear-up">
            <ShoppingBag size={15} /> Gear up
          </a>
        )}
      </div>
    </motion.div>
  );
}
